import { useState, useMemo, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { api, formatApiError } from "@/lib/api";
import { Seo } from "@/components/Seo";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { BOOK_REASONS } from "@/lib/cta";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CheckCircle2, ShieldCheck, Clock } from "lucide-react";

const SLOTS = ["09:00", "09:30", "10:00", "10:30", "11:00", "11:30", "13:00", "13:30", "14:00", "14:30", "15:00", "15:30", "16:00", "16:30"];

function upcomingDates(count = 12) {
  const out = [];
  const d = new Date();
  d.setDate(d.getDate() + 1);
  while (out.length < count) {
    const day = d.getDay(); // 0 Sun .. 6 Sat (UAE weekend Sat/Sun)
    if (day !== 0 && day !== 6) out.push(new Date(d));
    d.setDate(d.getDate() + 1);
  }
  return out;
}

const fmtISO = (d) => d.toISOString().slice(0, 10);
const fmtDay = (d) => d.toLocaleDateString("en-GB", { weekday: "short" });
const fmtDate = (d) => d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });

const empty = { name: "", business_email: "", company: "", job_title: "" };

export default function BookConsultation() {
  const [params] = useSearchParams();
  const dates = useMemo(() => upcomingDates(12), []);
  const [date, setDate] = useState(null);
  const [time, setTime] = useState(null);
  const [reason, setReason] = useState(BOOK_REASONS.includes(params.get("reason")) ? params.get("reason") : "");
  const [form, setForm] = useState(empty);
  const [status, setStatus] = useState("idle");

  useEffect(() => { window.scrollTo(0, 0); }, []);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const field = "bg-transparent border-0 border-b border-white/20 rounded-none px-0 h-12 text-bone placeholder:text-muted2/60 focus-visible:ring-0 focus-visible:border-gold text-base";

  const submit = async (e) => {
    e.preventDefault();
    if (!date || !time) { toast.error("Please choose a date and time."); return; }
    if (!form.name || !form.business_email || !reason) { toast.error("Please complete the required fields."); return; }
    setStatus("submitting");
    try {
      await api.post("/bookings", { ...form, reason, date: fmtISO(date), time });
      if (window.gtag) window.gtag("event", "consultation_booking", { reason, date: fmtISO(date), time });
      setStatus("done");
      window.scrollTo(0, 0);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setStatus("idle");
    }
  };

  return (
    <div data-testid="book-consultation-page">
      <Seo
        title="Book an Employee Relations Consultation"
        description="Schedule a complimentary 20-minute introductory call to discuss a complex ER matter, workplace investigation, People Risk challenge or the ER capability your organisation needs to build."
        path="/book-consultation"
      />
      <Header />
      <main className="pt-32 pb-28 px-6 md:px-12 min-h-screen">
        <div className="max-w-5xl mx-auto">
          {status === "done" ? (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-2xl mx-auto text-center py-16" data-testid="booking-confirmation">
              <div className="w-16 h-16 rounded-full border border-gold/40 flex items-center justify-center mx-auto mb-8">
                <CheckCircle2 className="text-gold" size={28} />
              </div>
              <h1 className="font-serif font-light text-4xl md:text-5xl tracking-tight mb-6">Your consultation request is in.</h1>
              <p className="text-muted2 text-lg leading-relaxed mb-4">
                Thank you, {form.name.split(" ")[0]}. I've received your request for <strong className="text-bone">{fmtDate(date)}</strong> at <strong className="text-bone">{time} (GST)</strong>.
              </p>
              <p className="text-muted2 leading-relaxed mb-10">
                You'll receive a personal email from me to confirm the time and share a calendar invite for our complimentary 20-minute introductory call. If anything is time-critical, mention it in your reply.
              </p>
              <Link to="/" data-testid="booking-home-link" className="inline-flex items-center gap-2 font-sans text-xs uppercase tracking-[0.2em] border border-white/20 text-bone px-8 py-4 hover:border-gold hover:text-gold transition-colors">
                Return Home
              </Link>
            </motion.div>
          ) : (
            <>
              <p className="font-mono text-[11px] tracking-[0.35em] text-gold uppercase mb-6">Book an Introductory Consultation</p>
              <h1 className="font-serif font-light text-4xl md:text-6xl tracking-tight leading-[1.05] mb-6" data-testid="booking-heading">
                Book an Employee Relations Consultation
              </h1>
              <p className="text-bone/90 text-lg md:text-xl max-w-3xl leading-relaxed mb-4 font-light">
                Schedule a <strong>complimentary 20-minute</strong> introductory call to discuss a complex ER matter, workplace investigation, People Risk challenge or the ER capability your organisation needs to build.
              </p>
              <p className="text-muted2 max-w-3xl leading-relaxed mb-12 text-sm">
                This introductory conversation is designed to understand your situation and determine how I can best support you. Detailed case review, investigation support and ER advisory are provided through the relevant advisory service.
              </p>

              <form onSubmit={submit} data-testid="booking-form" className="grid grid-cols-1 lg:grid-cols-2 gap-16">
                <div>
                  <h2 className="font-mono text-[11px] tracking-[0.3em] uppercase text-muted2 mb-6 flex items-center gap-2"><Clock size={14} /> Choose a date &amp; time (GST)</h2>
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mb-8">
                    {dates.map((d) => {
                      const active = date && fmtISO(d) === fmtISO(date);
                      return (
                        <button type="button" key={fmtISO(d)} data-testid={`date-${fmtISO(d)}`}
                          onClick={() => { setDate(d); setTime(null); }}
                          className={`flex flex-col items-center py-3 border transition-colors ${active ? "border-gold bg-gold/10 text-gold" : "border-white/15 text-bone/80 hover:border-white/40"}`}>
                          <span className="font-mono text-[10px] uppercase tracking-wide">{fmtDay(d)}</span>
                          <span className="font-serif text-base mt-1">{fmtDate(d)}</span>
                        </button>
                      );
                    })}
                  </div>
                  {date && (
                    <div className="flex flex-wrap gap-2" data-testid="time-slots">
                      {SLOTS.map((t) => (
                        <button type="button" key={t} data-testid={`time-${t}`} onClick={() => setTime(t)}
                          className={`px-4 py-2.5 border font-mono text-sm transition-colors ${time === t ? "border-gold bg-gold text-ink" : "border-white/15 text-bone/80 hover:border-gold hover:text-gold"}`}>
                          {t}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <div className="space-y-7">
                  <h2 className="font-mono text-[11px] tracking-[0.3em] uppercase text-muted2 mb-1">Your details</h2>
                  <Input data-testid="booking-name" className={field} placeholder="Full name *" value={form.name} onChange={(e) => set("name", e.target.value)} />
                  <Input data-testid="booking-email" type="email" className={field} placeholder="Business email *" value={form.business_email} onChange={(e) => set("business_email", e.target.value)} />
                  <Input data-testid="booking-company" className={field} placeholder="Company / organisation" value={form.company} onChange={(e) => set("company", e.target.value)} />
                  <Input data-testid="booking-jobtitle" className={field} placeholder="Job title / role" value={form.job_title} onChange={(e) => set("job_title", e.target.value)} />
                  <Select value={reason} onValueChange={setReason}>
                    <SelectTrigger data-testid="booking-reason" className={field + " w-full justify-between"}>
                      <SelectValue placeholder="What would you like to discuss? *" />
                    </SelectTrigger>
                    <SelectContent className="bg-elevated border-white/15 text-bone">
                      {BOOK_REASONS.map((r) => <SelectItem key={r} value={r} className="focus:bg-gold focus:text-ink">{r}</SelectItem>)}
                    </SelectContent>
                  </Select>

                  <div className="flex items-start gap-3 text-muted2 text-xs leading-relaxed border border-white/12 p-4">
                    <ShieldCheck size={26} className="text-gold shrink-0" />
                    <span>Please do not submit confidential employee information, names or detailed case evidence through this form. Sensitive case details can be discussed confidentially during the consultation.</span>
                  </div>

                  <button type="submit" disabled={status === "submitting"} data-testid="booking-submit"
                    className="w-full font-sans text-xs uppercase tracking-[0.2em] bg-gold text-ink py-4 hover:bg-goldhover transition-colors disabled:opacity-50">
                    {status === "submitting" ? "Sending…" : "Book a Consultation"}
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
